<script lang="ts" setup>
import { computed, nextTick } from "vue";
import { useRouter } from "vue-router";

import { UNGROUPED_PSEUDO_GROUP_BY } from "~/common/groupBy";
import { getShownPropertyWithConfigList } from "~/common/properties";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import { notify } from "~/components/notifications";
import {
  convertDocToMarkdown,
  convertPageToMarkdown,
  convertPageToTable,
  convertTableToCsv,
  convertTaskToMarkdown,
  getExportNewWindowScriptContent,
  saveAsCsv,
  saveAsHtml,
  saveAsMarkdown,
  saveAsPdf,
} from "~/components/text/transformers";
import { ExportIcon } from "~/icons";
import { DropdownMenuItemKind, LayoutKind, NotificationType, Placement, PropertyKind } from "~/shared/enums";
import type { Doc, Page, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { getPageLink } from "~/utils/common";
import { layoutStable, when } from "~/utils/wait";

const WIDE_PROPERTY_KINDS = [PropertyKind.DEFAULT_TITLE, PropertyKind.DEFAULT_DESCRIPTION];
const WIDE_MULTIPLIER = 3;

const props = defineProps<{
  task?: Task;
  doc?: Doc;
  page?: Page;
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const exportInNewTab = async (print: boolean) => {
  if (!props.page) {
    return;
  }
  const content = document.getElementById("dartboard");
  if (!content) {
    return;
  }

  const oldHeight = document.body.clientHeight;
  const oldWidth = document.body.clientWidth;

  const gridApi = (appStore.list ?? appStore.roadmap)?.api;
  if (gridApi) {
    gridApi?.setGridOption("domLayout", "print");
    await when(() => gridApi.isAnimationFrameQueueEmpty());
  }

  try {
    const contentRect = content.getBoundingClientRect();
    let width = Math.max(
      contentRect.width,
      800,
      ...Array.from(content.getElementsByClassName("dart-scroll-width-container")).map((e) => {
        const rect = e.getBoundingClientRect();
        return e.scrollWidth + (rect.left - contentRect.left) + (contentRect.right - rect.right);
      })
    );
    let height = Math.max(
      contentRect.height,
      600,
      ...Array.from(content.getElementsByClassName("dart-scroll-height-container")).map((e) => {
        const rect = e.getBoundingClientRect();
        return e.scrollHeight + (rect.top - contentRect.top) + (contentRect.bottom - rect.bottom);
      })
    );

    let roadmapConfig: { leftWidth: number; rightWidth: number; scrollLeft: number } | undefined;
    if (appStore.layoutKind === LayoutKind.ROADMAP) {
      const list = content.getElementsByClassName("ag-root")[0];
      const leftWidth =
        list.scrollWidth +
        (list.getBoundingClientRect().left - contentRect.left) +
        (gridApi?.getColumns()?.length ?? 0) -
        1;
      const timeline = content.getElementsByClassName("dart-roadmap-timeline")[0];

      const { scrollLeft } = timeline;
      const rightPanel = content.getElementsByClassName("dart-roadmap-panes")[0].children[2];
      const rightWidth = Math.max(width - leftWidth, rightPanel.clientWidth, 800);
      if (width !== leftWidth + rightWidth) {
        width = leftWidth + rightWidth;
      }
      roadmapConfig = { leftWidth, rightWidth, scrollLeft };
    }

    document.body.style.height = `${height}px`;
    document.body.style.width = `${width}px`;
    await layoutStable(document.body, 100);

    if (appStore.layoutKind === LayoutKind.BOARD) {
      for (let i = 0; i < 100; i += 1) {
        const newContentRect = content.getBoundingClientRect();
        const newHeight = Math.max(
          height,
          ...Array.from(content.getElementsByClassName("dart-scroll-height-container")).map((e) => {
            const rect = e.getBoundingClientRect();
            return e.scrollHeight + (rect.top - newContentRect.top) + (newContentRect.bottom - rect.bottom);
          })
        );
        if (newHeight === height) {
          break;
        }
        height = newHeight;
        document.body.style.height = `${height}px`;
        // eslint-disable-next-line no-await-in-loop
        await layoutStable(document.body, 100);
      }
    }

    const contentClone = content.cloneNode(true) as HTMLElement;
    Array.from(contentClone.getElementsByClassName("print:hidden")).forEach((e) => e.remove());
    if (roadmapConfig) {
      const { leftWidth, rightWidth, scrollLeft } = roadmapConfig;

      const panelsClone = contentClone.getElementsByClassName("dart-roadmap-panes")[0];
      (panelsClone.children[0] as HTMLElement).style.width = `${leftWidth}px`;
      (panelsClone.children[2] as HTMLElement).style.width = `${rightWidth}px`;

      const timelineClone = contentClone.getElementsByClassName("dart-roadmap-timeline")[0] as HTMLElement;
      const rangeClone = contentClone.getElementsByClassName("dart-roadmap-range")[0] as HTMLElement;
      [timelineClone, rangeClone].forEach((el) => {
        Array.from(el.children).forEach((child) => {
          // eslint-disable-next-line no-param-reassign
          (child as HTMLElement).style.transform = `translateX(-${scrollLeft}px)`;
        });
      });
      timelineClone.style.overflow = "hidden";
    }

    const printWindow = window.open("", "_blank", "width=800px,height=600px");
    if (!printWindow) {
      notify({
        message: "To export, allow popups in your browser",
        type: NotificationType.WARNING,
      });
      return;
    }

    const headContent = Array.from(document.head.children)
      .filter((el) => el.tagName === "STYLE" || (el.tagName === "LINK" && (el as HTMLLinkElement).rel === "stylesheet"))
      .map((el) => el.outerHTML)
      .join("\n");

    printWindow.document.open();
    printWindow.document.write(`
      <html class="${pageStore.theme} overflow-visible" style="width: ${width}px; height: ${height}px;">
        <head>
          <title>${props.page.title}</title>
          ${headContent}
          ${getExportNewWindowScriptContent(props.page.title, print, width, height)}
        </head>
        <body class="size-full flex flex-col subpixel-antialiased bg-std selection:bg-indigo-200 dark:selection:bg-indigo-900">
          ${contentClone.outerHTML}
          <div class="absolute inset-0 flex flex-col justify-evenly items-center bg-std">
            <div class="dart-message text-md text-2xl font-semibold">
              Exporting...
            </div>
            <div />
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  } finally {
    document.body.style.height = `${oldHeight}px`;
    document.body.style.width = `${oldWidth}px`;
    gridApi?.setGridOption("domLayout", "normal");
  }
};

const normalizeTables = (html: string) => {
  const properties = getShownPropertyWithConfigList(WIDE_PROPERTY_KINDS);
  const total =
    properties.length +
    (WIDE_MULTIPLIER - 1) * properties.filter(([property]) => WIDE_PROPERTY_KINDS.includes(property.kind)).length;
  const wide = (100 * WIDE_MULTIPLIER) / total;
  const normal = 100 / total;
  const origFrags: string[] = [];
  const newFrags: string[] = [];
  properties.forEach(([property]) => {
    origFrags.push(`<th>${property.title}</th>`);
    newFrags.push(
      `<th style="width: ${WIDE_PROPERTY_KINDS.includes(property.kind) ? wide : normal}%;">${property.title}</th>`
    );
  });
  return html.replaceAll(origFrags.join("\n"), newFrags.join("\n"));
};

const getPageMarkdown = (page: Page) => {
  const groupedTasks: { title: string; tasks: Task[] }[] = [];
  const collapsedColumnIds = new Set(appStore.collapsedGroups);
  const { groupByDefinition, hideEmptyGroups } = appStore;
  if (groupByDefinition.property.duid === UNGROUPED_PSEUDO_GROUP_BY) {
    groupedTasks.push({
      title: "All",
      // TODO incremental get all of the tasks in the page, don't just use the loaded ones
      tasks: dataStore.getLoadedTasksForPageDuidOrdered(page.duid),
    });
  } else {
    groupByDefinition.groups.forEach((group) => {
      if (collapsedColumnIds.has(group.id)) {
        return;
      }
      // TODO incremental get all of the tasks in the page, don't just use the loaded ones
      const tasks = dataStore.getLoadedTasksForPageDuidOrdered(page.duid, group.id);
      if (hideEmptyGroups && tasks.length === 0) {
        return;
      }
      groupedTasks.push({ title: group.title, tasks });
    });
  }
  return convertPageToMarkdown(page, groupedTasks, getShownPropertyWithConfigList);
};

const exportAsImage = async () => {
  await exportInNewTab(false);
};

const exportAsPdf = async () => {
  if (props.task) {
    const markdown = await convertTaskToMarkdown(props.task, getShownPropertyWithConfigList, dataStore);
    await saveAsPdf(markdown, props.task.title);
    return;
  }
  if (props.doc) {
    await saveAsPdf(convertDocToMarkdown(props.doc), props.doc.title);
    return;
  }
  if (props.page) {
    await exportInNewTab(true);
  }
};

const exportAsCsv = async () => {
  if (props.page) {
    await router.push(getPageLink(props.page, dataStore.getSpaceByDuid));
    await nextTick();
    saveAsCsv(
      convertTableToCsv(
        // TODO incremental get all of the tasks in the page, don't just use the loaded ones
        convertPageToTable(dataStore.getLoadedTasksForPageDuidOrdered(props.page.duid), getShownPropertyWithConfigList)
      ),
      props.page.title
    );
  }
};

const exportAsHtml = async () => {
  if (props.task) {
    const markdown = await convertTaskToMarkdown(props.task, getShownPropertyWithConfigList, dataStore);
    saveAsHtml(markdown, props.task.title);
    return;
  }
  if (props.doc) {
    saveAsHtml(convertDocToMarkdown(props.doc), props.doc.title);
    return;
  }
  if (props.page) {
    await router.push(getPageLink(props.page, dataStore.getSpaceByDuid));
    await nextTick();
    saveAsHtml(getPageMarkdown(props.page), props.page.title, {
      postprocessHtml: normalizeTables,
    });
  }
};

const exportAsMarkdown = async () => {
  if (props.task) {
    const markdown = await convertTaskToMarkdown(props.task, getShownPropertyWithConfigList, dataStore);
    saveAsMarkdown(markdown, props.task.title);
    return;
  }
  if (props.doc) {
    saveAsMarkdown(convertDocToMarkdown(props.doc), props.doc.title);
  }
};

const sections = computed(() => [
  {
    title: "Export as",
    items: [
      {
        title: "Image",
        hidden: !props.page,
        kind: DropdownMenuItemKind.BUTTON,
        onClick: exportAsImage,
      },
      {
        title: "PDF",
        kind: DropdownMenuItemKind.BUTTON,
        onClick: exportAsPdf,
      },
      {
        title: "CSV",
        hidden: !props.page,
        kind: DropdownMenuItemKind.BUTTON,
        onClick: exportAsCsv,
      },
      {
        title: "HTML",
        kind: DropdownMenuItemKind.BUTTON,
        onClick: exportAsHtml,
      },
      {
        title: "Markdown",
        hidden: !props.task && !props.doc,
        kind: DropdownMenuItemKind.BUTTON,
        onClick: exportAsMarkdown,
      },
    ],
  },
]);
</script>

<template>
  <DropdownMenu
    :sections="sections"
    :placement="Placement.RIGHT_TOP"
    :width-pixels="120"
    show-on-hover
    mobile-include-back>
    <DropdownMenuItemContent :icon="ExportIcon" title="Export as" is-submenu />
  </DropdownMenu>
</template>
